import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { 
  Users, 
  UserPlus, 
  Settings, 
  Search, 
  Loader2, 
  AlertCircle, 
  CheckCircle, 
  XCircle, 
  UserCog 
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../ui/table';
import { useApiRequest } from '../../hooks/useApiRequest';
import { PaginationWrapper } from '../ui/PaginationWrapper';

interface MarketingManager {
  _id: string;
  name: string;
  email: string;
  phone: string;
  totalLeads: number;
  leadLimit: number;
  canSeeUnassigned: boolean;
  isActive: boolean;
  createdAt: string;
}

interface Lead {
  _id: string;
  userId?: string;
  name: string;
  email: string;
  phone: string;
  gender: string;
  city: string;
  state: string;
  country: string;
  registeredDate: string;
  plan: string;
  assignedDate?: string;
  assignedBy?: string;
  status?: string;
  isAssignedToTeam?: boolean;
  teamMember?: string;
  adminNotes?: string;
}

export default function AdminMarketingManagerLeads() {
  const [managers, setManagers] = useState<MarketingManager[]>([]);
  const [selectedManager, setSelectedManager] = useState<MarketingManager | null>(null);
  const [availableLeads, setAvailableLeads] = useState<Lead[]>([]);
  const [assignedLeads, setAssignedLeads] = useState<Lead[]>([]);
  const [selectedLeads, setSelectedLeads] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('managers');
  
  // Dialogs
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);
  const [isSettingsDialogOpen, setIsSettingsDialogOpen] = useState(false);
  const [isViewLeadsDialogOpen, setIsViewLeadsDialogOpen] = useState(false);
  
  // Settings form
  const [leadLimit, setLeadLimit] = useState(100);
  const [canSeeUnassigned, setCanSeeUnassigned] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [adminNotes, setAdminNotes] = useState('');

  const { loading, execute } = useApiRequest();

  const fetchManagers = async () => {
    const response = await execute(`/admin/manager-leads/managers`);

    if (response?.success) {
      const managersData = Array.isArray(response.data) ? response.data : (response.data?.managers || []);
      setManagers(Array.isArray(managersData) ? managersData : []);
    }
  };

  useEffect(() => {
    void fetchManagers();
  }, []);

  const fetchAvailableLeads = async () => {
    const response = await execute(`/admin/manager-leads/available?limit=100`);

    if (response?.success) {
      setAvailableLeads(response.data?.leads || []);
    }
  };

  const fetchManagerLeads = async (managerId: string) => {
    const response = await execute(`/admin/manager-leads/manager/${managerId}/leads`);

    if (response?.success) {
      setAssignedLeads(response.data?.leads || []);
    }
  };

  const fetchManagerSettings = async (managerId: string) => {
    const response = await execute(`/admin/manager-leads/settings/${managerId}`);

    if (response?.success && response.data?.settings) {
      setLeadLimit(response.data.settings.leadLimit || 100);
      setCanSeeUnassigned(response.data.settings.canSeeUnassignedLeads || false);
      setIsActive(response.data.settings.isActive !== false);
    }
  };

  const handleOpenAssignDialog = async (manager: MarketingManager) => {
    setSelectedManager(manager);
    setSelectedLeads([]);
    await fetchAvailableLeads();
    setIsAssignDialogOpen(true);
  };

  const handleOpenSettingsDialog = async (manager: MarketingManager) => {
    setSelectedManager(manager);
    await fetchManagerSettings(manager._id);
    setIsSettingsDialogOpen(true);
  };

  const handleOpenViewLeadsDialog = async (manager: MarketingManager) => {
    setSelectedManager(manager);
    await fetchManagerLeads(manager._id);
    setIsViewLeadsDialogOpen(true);
  };

  const handleAssignLeads = async () => {
    if (!selectedManager || selectedLeads.length === 0) {
      toast.error('Please select at least one lead');
      return;
    }

    const response = await execute(`/admin/manager-leads/assign`, {
      method: 'POST',
      data: {
        marketingManagerId: selectedManager._id,
        userIds: selectedLeads,
        adminNotes
      }
    });

    if (response?.success) {
      toast.success(`Successfully assigned ${response.data?.assigned} lead(s) to ${selectedManager.name}`);
      setIsAssignDialogOpen(false);
      setSelectedLeads([]);
      setAdminNotes('');
      await fetchManagers();
    }
  };

  const handleUpdateSettings = async () => {
    if (!selectedManager) return;

    const response = await execute(`/admin/manager-leads/settings/${selectedManager._id}`, {
      method: 'PUT',
      data: {
        leadLimit,
        canSeeUnassignedLeads: canSeeUnassigned,
        isActive
      }
    });

    if (response?.success) {
      toast.success('Settings updated successfully');
      setIsSettingsDialogOpen(false);
      await fetchManagers();
    }
  };

  const _handleUnassignLeads = async (assignmentIds: string[]) => {
    if (assignmentIds.length === 0) return;

    const response = await execute(`/admin/manager-leads/unassign`, {
      method: 'DELETE',
      data: { assignmentIds }
    });

    if (response?.success) {
      toast.success(`Unassigned ${response.data?.unassigned} lead(s)`);
      if (selectedManager) {
        await fetchManagerLeads(selectedManager._id);
      }
    }
  };

  const toggleLeadSelection = (leadId: string) => {
    setSelectedLeads(prev => 
      prev.includes(leadId) 
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  const filteredAvailableLeads = availableLeads.filter(lead =>
    lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    lead.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    lead.phone.includes(searchTerm) ||
    lead.city.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredManagers = managers.filter(manager =>
    manager.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    manager.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Show empty state if no managers exist at all
  const hasNoManagers = !loading && managers.length === 0;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl">Marketing Manager Leads</h1>
          <p className="text-muted-foreground mt-1">
            Assign and manage leads for marketing managers
          </p>
        </div>
      </div>

      {hasNoManagers ? (
        <Card>
          <CardContent className="py-16">
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <div className="p-4 bg-purple-100 rounded-full">
                  <UserCog className="w-12 h-12 text-purple-600" />
                </div>
              </div>
              <h2 className="text-xl font-semibold mb-2">No Marketing Managers Found</h2>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                Before you can assign leads, you need to create marketing managers. 
                Marketing managers can manage teams and handle lead assignments.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  onClick={() => {
                    // Navigate to role management
                    const roleManagementBtn = document.querySelector('[data-view="role-management"]') as HTMLButtonElement;
                    if (roleManagementBtn) {
                      roleManagementBtn.click();
                    }
                  }}
                  className="gap-2"
                >
                  <UserCog className="w-4 h-4" />
                  Go to Role Management
                </Button>
                <Button
                  variant="outline"
                  onClick={() => fetchManagers()}
                >
                  <Loader2 className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </div>
              <div className="mt-6 p-4 bg-blue-50 rounded-lg text-left max-w-2xl mx-auto">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-blue-900">
                    <p className="font-semibold mb-1">How to create a Marketing Manager:</p>
                    <ol className="list-decimal list-inside space-y-1 ml-2">
                      <li>Go to <strong>Role Management</strong> from the admin sidebar</li>
                      <li>Find a user or create a new account</li>
                      <li>Click <strong>Change Role</strong> and select <strong>Marketing Manager</strong></li>
                      <li>Return here to assign leads to your new marketing manager</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="managers">
              <Users className="w-4 h-4 mr-2" />
              Managers
            </TabsTrigger>
          </TabsList>

          <TabsContent value="managers" className="space-y-4 mt-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Marketing Managers</CardTitle>
                    <CardDescription>
                      Manage lead assignments and settings for each marketing manager
                    </CardDescription>
                  </div>
                  <div className="w-64">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder="Search managers..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-9"
                      />
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loading && managers.length === 0 ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : filteredManagers.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="w-12 h-12 mx-auto mb-2 opacity-20" />
                    <p>No marketing managers found</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Manager</TableHead>
                        <TableHead>Contact</TableHead>
                        <TableHead>Leads</TableHead>
                        <TableHead>Lead Limit</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredManagers.map((manager) => (
                        <TableRow key={manager._id}>
                          <TableCell>
                            <div>
                              <div>{manager.name}</div>
                              <div className="text-sm text-muted-foreground">{manager.email}</div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">{manager.phone || 'N/A'}</div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <span>{manager.totalLeads}</span>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleOpenViewLeadsDialog(manager)}
                              >
                                View
                              </Button>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {manager.leadLimit} max
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {manager.isActive ? (
                              <Badge variant="default" className="bg-green-500">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Active
                              </Badge>
                            ) : (
                              <Badge variant="secondary">
                                <XCircle className="w-3 h-3 mr-1" />
                                Inactive
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleOpenAssignDialog(manager)}
                                disabled={manager.totalLeads >= manager.leadLimit}
                              >
                                <UserPlus className="w-4 h-4 mr-2" />
                                Assign Leads
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleOpenSettingsDialog(manager)}
                              >
                                <Settings className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}

      {/* Assign Leads Dialog */}
      <Dialog open={isAssignDialogOpen} onOpenChange={setIsAssignDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Assign Leads to {selectedManager?.name}</DialogTitle>
            <DialogDescription>
              Current: {selectedManager?.totalLeads || 0} / {selectedManager?.leadLimit || 0} leads
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search available leads..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>

            <div className="space-y-2">
              <Label>Admin Notes (optional)</Label>
              <Input
                placeholder="Add notes about these leads..."
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
              />
            </div>

            <div className="border rounded-lg max-h-96 overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox
                        checked={selectedLeads.length === filteredAvailableLeads.length && filteredAvailableLeads.length > 0}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedLeads(filteredAvailableLeads.map(l => l._id));
                          } else {
                            setSelectedLeads([]);
                          }
                        }}
                      />
                    </TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Plan</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" />
                      </TableCell>
                    </TableRow>
                  ) : filteredAvailableLeads.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                        No available leads found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredAvailableLeads.map((lead) => (
                      <TableRow key={lead._id}>
                        <TableCell>
                          <Checkbox
                            checked={selectedLeads.includes(lead._id)}
                            onCheckedChange={() => toggleLeadSelection(lead._id)}
                          />
                        </TableCell>
                        <TableCell>{lead.name}</TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <div>{lead.email}</div>
                            <div className="text-muted-foreground">{lead.phone}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {lead.city}, {lead.state}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={lead.plan === 'Active' ? 'default' : 'secondary'}>
                            {lead.plan}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <AlertCircle className="w-4 h-4" />
              <span>{selectedLeads.length} lead(s) selected</span>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAssignDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAssignLeads}
              disabled={selectedLeads.length === 0 || loading}
            >
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Assign {selectedLeads.length} Lead(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={isSettingsDialogOpen} onOpenChange={setIsSettingsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Lead Settings - {selectedManager?.name}</DialogTitle>
            <DialogDescription>
              Configure lead management settings for this marketing manager
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Lead Limit</Label>
              <Input
                type="number"
                value={leadLimit}
                onChange={(e) => setLeadLimit(parseInt(e.target.value) || 0)}
                min={0}
              />
              <p className="text-xs text-muted-foreground">
                Maximum number of leads this manager can have
              </p>
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Can See Unassigned Leads</Label>
                <p className="text-xs text-muted-foreground">
                  Allow manager to see all unassigned users
                </p>
              </div>
              <Checkbox
                checked={canSeeUnassigned}
                onCheckedChange={(checked) => setCanSeeUnassigned(!!checked)}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Active Status</Label>
                <p className="text-xs text-muted-foreground">
                  Enable or disable this manager's access
                </p>
              </div>
              <Checkbox
                checked={isActive}
                onCheckedChange={(checked) => setIsActive(!!checked)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsSettingsDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateSettings} disabled={loading}>
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Assigned Leads Dialog */}
      <Dialog open={isViewLeadsDialogOpen} onOpenChange={setIsViewLeadsDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Assigned Leads - {selectedManager?.name}</DialogTitle>
            <DialogDescription>
              Leads currently assigned to this marketing manager
            </DialogDescription>
          </DialogHeader>

          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Assigned Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Team Member</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" />
                    </TableCell>
                  </TableRow>
                ) : assignedLeads.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      No leads assigned yet
                    </TableCell>
                  </TableRow>
                ) : (
                  assignedLeads.map((lead) => (
                    <TableRow key={lead._id}>
                      <TableCell>{lead.name}</TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <div>{lead.email}</div>
                          <div className="text-muted-foreground">{lead.phone}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">{lead.city}, {lead.state}</div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {lead.assignedDate ? new Date(lead.assignedDate).toLocaleDateString() : 'N/A'}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={lead.status === 'active' ? 'default' : 'secondary'}>
                          {lead.status || 'active'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {lead.isAssignedToTeam ? lead.teamMember : 'Unassigned'}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsViewLeadsDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}